import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePresupuestoDetalle, usePresupuestos } from '@/hooks/usePresupuestos';
import { cliente } from '@/lib/cliente';
import type { PresupuestoResumen } from '@adelante/shared';
import {
  ArrowClockwise,
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  CircleNotch,
  Eye,
  Receipt,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { toast } from 'sonner';

/** Formatea un monto en colones: ₡ 40 236 838,80 */
function crc(n: number): string {
  return `₡ ${n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Orden por columna de la lista de presupuestos.
type PCol = 'works_no' | 'version_code' | 'total_costo' | 'fecha_carga' | 'estado';
type POrden = { col: PCol; dir: 'asc' | 'desc' };

function valorP(f: PresupuestoResumen, col: PCol): string | number {
  switch (col) {
    case 'works_no':
      return f.works_no;
    case 'version_code':
      return f.version_code;
    case 'total_costo':
      return f.total_costo;
    case 'fecha_carga':
      return f.fecha_carga;
    case 'estado':
      return f.es_ultima_version ? 1 : 0;
  }
}

function fecha(iso: string): string {
  return iso.slice(0, 10).split('-').reverse().join('/');
}

/**
 * Admin · Presupuestos — versiones de presupuesto por obra desde
 * bi.fact_presupuesto (AdelanteDB). Lista con versión Vigente/Anterior y un
 * detalle (resumen por grupo + partidas). Base de los reportes de costos.
 */
export function PresupuestosPantalla() {
  const { data, isLoading, error } = usePresupuestos();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<{ obra: string; version: string } | null>(null);
  const [orden, setOrden] = useState<POrden | null>(null);
  const qc = useQueryClient();

  const etlQ = useQuery({
    queryKey: ['etl-estado'],
    queryFn: () => cliente.etl.estado(),
    refetchInterval: (query) => (query.state.data?.data?.estado === 'Running' ? 5_000 : false),
  });
  const etl = etlQ.data?.data;
  const corriendo = etl?.estado === 'Running';

  const ejecutarM = useMutation({
    mutationFn: () => cliente.etl.ejecutar(),
    onSuccess: () => {
      toast.success('ETL iniciado — la actualización tarda unos minutos.');
      qc.invalidateQueries({ queryKey: ['etl-estado'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo iniciar el ETL'),
  });

  function ordenarP(col: PCol) {
    setOrden((prev) =>
      prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
    );
  }

  const filas = data?.data ?? [];
  const vista = useMemo(() => {
    const t = q.trim().toLowerCase();
    let lista = t ? filas.filter((f) => f.works_no.toLowerCase().includes(t)) : filas;
    if (orden) {
      const dir = orden.dir === 'asc' ? 1 : -1;
      lista = [...lista].sort((a, b) => {
        const av = valorP(a, orden.col);
        const bv = valorP(b, orden.col);
        const c =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), 'es', { numeric: true });
        return dir * c;
      });
    }
    return lista;
  }, [filas, q, orden]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Presupuestos</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Versiones importadas desde Business Central (<code>bi.fact_presupuesto</code>). Cada obra
          puede tener varias versiones; la <strong>vigente</strong> alimenta los reportes.
        </p>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
        <div className="text-sm">
          <p className="font-medium">Actualización desde Business Central</p>
          <p className="text-muted-foreground">
            {corriendo ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-amber-700">
                <CircleNotch
                  className="h-3.5 w-3.5 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Actualizando… (puede tardar varios minutos)
              </span>
            ) : etl?.ultima_carga_bd ? (
              <>Última carga: {fecha(etl.ultima_carga_bd)}</>
            ) : etlQ.isLoading ? (
              'Consultando estado…'
            ) : (
              'Sin datos de la última carga'
            )}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => ejecutarM.mutate()}
          disabled={ejecutarM.isPending || corriendo}
          title="Corre el mismo proceso que se ejecuta automáticamente cada noche — no hay que esperar al día siguiente"
        >
          <ArrowClockwise
            className={corriendo || ejecutarM.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
            weight="bold"
            aria-hidden="true"
          />
          {corriendo ? 'Actualizando…' : ejecutarM.isPending ? 'Iniciando…' : 'Actualizar ahora'}
        </Button>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" weight="bold" aria-hidden="true" />
            Presupuestos por obra
            <span className="text-sm font-normal text-muted-foreground">{vista.length}</span>
          </CardTitle>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar obra…"
            className="h-9 w-56"
          />
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando presupuestos…
            </div>
          )}
          {error && <p className="py-8 text-destructive">Error: {error.message}</p>}

          {!isLoading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <ThSortP col="works_no" orden={orden} onOrdenar={ordenarP}>
                    Obra
                  </ThSortP>
                  <ThSortP col="version_code" orden={orden} onOrdenar={ordenarP}>
                    Versión
                  </ThSortP>
                  <ThSortP col="total_costo" orden={orden} onOrdenar={ordenarP} align="right">
                    Total presupuestado
                  </ThSortP>
                  <ThSortP col="fecha_carga" orden={orden} onOrdenar={ordenarP}>
                    Fecha carga
                  </ThSortP>
                  <ThSortP col="estado" orden={orden} onOrdenar={ordenarP}>
                    Estado
                  </ThSortP>
                  <TableHead className="text-right">Detalle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vista.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No hay presupuestos.
                    </TableCell>
                  </TableRow>
                )}
                {vista.map((f, i) => {
                  const nuevaObra = i === 0 || vista[i - 1]?.works_no !== f.works_no;
                  return (
                    <TableRow key={`${f.works_no}|${f.version_code}`}>
                      <TableCell className="font-mono text-xs font-semibold">
                        {nuevaObra ? f.works_no : ''}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {f.version_code}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm">
                        {crc(f.total_costo)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fecha(f.fecha_carga)}
                      </TableCell>
                      <TableCell>
                        {f.es_ultima_version ? (
                          <Badge className="bg-adelante-primary text-adelante-foreground">
                            Vigente
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Anterior</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSel({ obra: f.works_no, version: f.version_code })}
                        >
                          <Eye className="h-4 w-4" weight="bold" aria-hidden="true" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DetalleDialog sel={sel} onClose={() => setSel(null)} />
    </div>
  );
}

function DetalleDialog({
  sel,
  onClose,
}: {
  sel: { obra: string; version: string } | null;
  onClose: () => void;
}) {
  const { data, isLoading } = usePresupuestoDetalle(sel?.obra ?? null, sel?.version);
  const d = data?.data;

  return (
    <Dialog open={sel !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle>Detalle del presupuesto</DialogTitle>
          <DialogDescription>{sel ? `${sel.obra} · Versión ${sel.version}` : ''}</DialogDescription>
        </DialogHeader>

        {isLoading || !d ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            Cargando…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-zinc-50 p-3 text-sm sm:grid-cols-3">
              <Dato label="Estado" valor={d.es_ultima_version ? 'Vigente' : 'Anterior'} />
              <Dato label="Fecha carga" valor={fecha(d.fecha_carga)} />
              <Dato label="Total presupuestado" valor={crc(d.total_costo)} />
            </div>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Resumen por grupos
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Código</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Peso %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.grupos.map((g) => (
                    <TableRow key={g.task_no}>
                      <TableCell className="font-mono text-xs">{g.task_no}</TableCell>
                      <TableCell>{g.descripcion}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm">
                        {crc(g.total)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {g.peso_pct.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Detalle de partidas
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Código</TableHead>
                    <TableHead>Partida</TableHead>
                    <TableHead className="text-right">Cant.</TableHead>
                    <TableHead className="text-right">Importe</TableHead>
                    <TableHead className="text-right">Peso %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.partidas.map((p) => (
                    <TableRow key={p.task_no}>
                      <TableCell className="font-mono text-xs">{p.task_no}</TableCell>
                      <TableCell>{p.descripcion}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {p.cantidad} {p.unidad ?? ''}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm">
                        {crc(p.importe)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {p.peso_pct.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Encabezado clickeable para ordenar la lista de presupuestos. */
function ThSortP({
  col,
  orden,
  onOrdenar,
  children,
  align = 'left',
}: {
  col: PCol;
  orden: POrden | null;
  onOrdenar: (col: PCol) => void;
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  const activo = orden?.col === col;
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => onOrdenar(col)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          align === 'right' ? 'flex-row-reverse' : ''
        }`}
      >
        {children}
        {activo ? (
          orden?.dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" weight="bold" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" weight="bold" aria-hidden="true" />
          )
        ) : (
          <ArrowsDownUp className="h-3 w-3 opacity-30" weight="bold" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );
}

function Dato({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-medium">{valor}</p>
    </div>
  );
}
